import { forwardRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFederationSchema, type CreateFederationInput } from '@proref/schemas';
import type { Federation } from '@proref/types';
import { PageHeader } from '../../components/shared/PageHeader';
import { Icon } from '../../components/shared/Icons';
import { Input } from '../../components/shared/Input';
import { Field } from '../../components/shared/Field';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { ListSkeleton } from '../../components/shared/Skeleton';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess } from '../../lib/toast';
import {
  useCreateFederation,
  useFederationsQuery,
  useUpdateFederation,
  useDeleteFederation,
} from '../../hooks/use-federations';

// Full IANA list when the runtime supports it (modern browsers), else a small curated fallback.
// Either way the value is validated by createFederationSchema before submit.
const TIMEZONES: string[] = (() => {
  try {
    const fn = (Intl as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    const zones = fn?.('timeZone');
    if (zones && zones.length) return zones;
  } catch {
    // fall through to the curated list
  }
  return ['UTC', 'Europe/Zagreb', 'Europe/Berlin', 'Europe/London', 'America/New_York'];
})();

// Timezone dropdown, shared by the create form and the edit dialog.
// forwardRef is REQUIRED here, not cosmetic: this is spread with react-hook-form's register(),
// whose returned ref is how RHF reads the live <select> value. A plain function component drops it
// silently — and the failure is delayed, which is what made it hard to see. Until the field is
// touched RHF serves the defaultValue and everything looks fine; the moment the admin PICKS a
// timezone, RHF's change handler can't resolve the field and stores undefined, so submit fails
// Zod with a bare "Required" under a select that visibly has a value selected.
const TimezoneSelect = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function TimezoneSelect({ id, ...rest }, ref) {
    return (
    <select
      ref={ref}
      id={id}
      className="h-10 w-full rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-ink-3"
      {...rest}
    >
      {TIMEZONES.map((tz) => (
        <option key={tz} value={tz}>
          {tz}
        </option>
      ))}
    </select>
    );
  },
);

// Edit a tenant's name / country / timezone in an AlertDialog. The timezone drives streak resets
// and daily selection, so it's an editable field here (not just a create-time choice).
function EditFederationDialog({ federation }: { federation: Federation }) {
  const { t } = useTranslation();
  const update = useUpdateFederation();
  const [open, setOpen] = useState(false);

  const defaults: CreateFederationInput = {
    name: federation.name,
    // Older tenants may predate the acronym column (null) — seed the field empty so it's editable.
    acronym: federation.acronym ?? '',
    country: federation.country,
    timezone: federation.timezone,
  };
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateFederationInput>({ resolver: zodResolver(createFederationSchema), defaultValues: defaults });

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) reset(defaults);
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      await update.mutateAsync({ id: federation.id, input: values });
      notifySuccess(t('admin.federations.updatedToast', { name: values.name }));
      onOpenChange(false);
    } catch (err) {
      notifyError(err, t('admin.federations.updateErrorToast'));
    }
  });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" icon aria-label={t('admin.federations.editAria', { name: federation.name })}>
          <Icon.pencil className="w-3.5 h-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.federations.editTitle', { name: federation.name })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('admin.federations.editDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-3.5" noValidate>
          <Field label={t('admin.federations.fieldName')} htmlFor="edit-fed-name" error={errors.name?.message}>
            <Input id="edit-fed-name" {...register('name')} />
          </Field>
          <Field label={t('admin.federations.fieldAcronym')} htmlFor="edit-fed-acronym" error={errors.acronym?.message}>
            <Input id="edit-fed-acronym" {...register('acronym')} />
          </Field>
          <Field label={t('admin.federations.fieldCountry')} htmlFor="edit-fed-country" error={errors.country?.message}>
            <Input id="edit-fed-country" {...register('country')} />
          </Field>
          <Field label={t('admin.federations.fieldTimezone')} htmlFor="edit-fed-timezone" error={errors.timezone?.message}>
            <TimezoneSelect id="edit-fed-timezone" {...register('timezone')} />
          </Field>
          <AlertDialogFooter className="mt-1">
            <AlertDialogCancel type="button" disabled={update.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <Button type="submit" variant="accent" disabled={update.isPending}>
              {update.isPending ? <Spinner className="w-4 h-4" /> : t('admin.users.editDialog.saveChanges')}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Delete-confirm for a tenant. The API refuses (409 FEDERATION_IN_USE) while it still has members,
// clips or exams — that message surfaces via notifyError so a live tenant can't be wiped by mistake.
function DeleteFederationDialog({ federation }: { federation: Federation }) {
  const { t } = useTranslation();
  const del = useDeleteFederation();
  const [open, setOpen] = useState(false);

  async function confirm() {
    try {
      await del.mutateAsync(federation.id);
      notifySuccess(t('admin.federations.deletedToast', { name: federation.name }));
      setOpen(false);
    } catch (err) {
      notifyError(err, t('admin.federations.deleteErrorToast'));
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" icon aria-label={t('admin.federations.deleteAria', { name: federation.name })}>
          <Icon.trash className="w-3.5 h-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.federations.deleteConfirmTitle', { name: federation.name })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('admin.federations.deleteConfirmBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={del.isPending}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <Button variant="danger" onClick={confirm} disabled={del.isPending}>
            {del.isPending ? <Spinner className="w-4 h-4" /> : t('admin.federations.deleteButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function FederationList() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useFederationsQuery();

  // First load only — no data yet. A background refetch (data already present) shows nothing
  // here; the global FetchingBar is the cue instead.
  if (isLoading) {
    return <ListSkeleton />;
  }

  // Only surface the error state when we have nothing cached to show — a failed *background*
  // refetch keeps the last-good list on screen rather than blanking it.
  if (isError && !data) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(error, t('admin.federations.couldNotLoad'))}
      </div>
    );
  }

  const federations = data ?? [];
  if (federations.length === 0) {
    return (
      <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-12 text-center">
        <p className="text-sm text-muted">{t('admin.federations.noFederationsYet')}</p>
      </div>
    );
  }

  return (
    // overflow-x-auto (not -hidden): five columns can't compress to a phone's width, so the table
    // keeps a readable floor and the container scrolls instead of crushing every cell.
    <div className="overflow-x-auto rounded border border-line bg-surface">
      <table className="w-full min-w-[720px] text-left text-[13px]">
        <thead>
          <tr className="border-b border-line text-[11px] uppercase tracking-[0.05em] text-muted">
            <th className="px-4 py-2.5 font-semibold">{t('admin.federations.colName')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.federations.colAcronym')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.federations.colCountry')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.federations.colTimezone')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.federations.colCreated')}</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {federations.map((fed: Federation) => (
            <tr key={fed.id} className="group border-b border-line last:border-0">
              <td className="px-4 py-2.5 font-medium text-ink">{fed.name}</td>
              <td className="px-4 py-2.5 font-mono text-[12px] text-ink-2">{fed.acronym ?? '—'}</td>
              <td className="px-4 py-2.5 text-ink-2">{fed.country}</td>
              <td className="px-4 py-2.5 font-mono text-[12px] text-muted">{fed.timezone}</td>
              <td className="px-4 py-2.5 font-mono text-[12px] text-muted">
                {new Date(fed.created_at).toLocaleDateString()}
              </td>
              {/* Row actions — subtle until hover so the table reads cleanly at a glance. */}
              <td className="px-4 py-2.5">
                <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <EditFederationDialog federation={fed} />
                  <DeleteFederationDialog federation={fed} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FederationsPage() {
  const { t } = useTranslation();
  const createFederation = useCreateFederation();
  const [created, setCreated] = useState<Federation | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateFederationInput>({
    resolver: zodResolver(createFederationSchema),
    defaultValues: { name: '', acronym: '', country: '', timezone: 'Europe/Zagreb' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const federation = await createFederation.mutateAsync(values);
      setCreated(federation);
      notifySuccess(t('admin.federations.createdToast', { name: federation.name }));
      reset({ name: '', acronym: '', country: '', timezone: values.timezone });
    } catch (err) {
      notifyError(err, t('admin.federations.createErrorToast'));
    }
  });

  return (
    <>
      <PageHeader
        title={t('admin.federations.title')}
        sub={t('admin.federations.sub')}
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* Create form */}
        <div className="rounded border border-line bg-surface p-5 shadow-1">
          <div className="mb-4 flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-sm bg-accent-tint text-accent">
              <Icon.plus className="w-4 h-4" />
            </div>
            <h2 className="text-[14px] font-semibold text-ink">{t('admin.federations.newFederationHeading')}</h2>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-3.5" noValidate>
            <Field label={t('admin.federations.fieldName')} htmlFor="name" error={errors.name?.message}>
              <Input id="name" placeholder={t('admin.federations.namePlaceholder')} {...register('name')} />
            </Field>
            <Field label={t('admin.federations.fieldAcronym')} htmlFor="acronym" error={errors.acronym?.message}>
              <Input id="acronym" placeholder={t('admin.federations.acronymPlaceholder')} {...register('acronym')} />
            </Field>
            <Field label={t('admin.federations.fieldCountry')} htmlFor="country" error={errors.country?.message}>
              <Input id="country" placeholder={t('admin.federations.countryPlaceholder')} {...register('country')} />
            </Field>
            <Field label={t('admin.federations.fieldTimezone')} htmlFor="timezone" error={errors.timezone?.message}>
              <TimezoneSelect id="timezone" {...register('timezone')} />
            </Field>
            <Button type="submit" variant="accent" disabled={createFederation.isPending} className="mt-1 self-start">
              {createFederation.isPending ? (
                <Spinner className="w-4 h-4" />
              ) : (
                <>
                  {t('admin.federations.createButton')} <Icon.arrow className="w-3.5 h-3.5" />
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Last created confirmation */}
        <div>
          {created ? (
            <div className="rounded border border-success/40 bg-success/10 p-4">
              <div className="mb-1 flex items-center gap-2 text-[13px] font-semibold text-ink">
                <Icon.check className="w-4 h-4 text-success" />
                {t('admin.federations.createdHeading', { name: created.name })}
              </div>
              <p className="text-[12.5px] text-muted">
                <Trans
                  i18nKey="admin.federations.createdBody"
                  values={{ country: created.country, timezone: created.timezone }}
                  components={{ code: <span className="font-mono" /> }}
                />
              </p>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center rounded border border-dashed border-line-2 bg-bg-2 p-6 text-center">
              <p className="max-w-xs text-[13px] text-muted">
                {t('admin.federations.noneYetHint')}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-7">
        <h2 className="mb-3 text-[13px] font-semibold uppercase tracking-[0.05em] text-muted">{t('admin.federations.allFederationsHeading')}</h2>
        <FederationList />
      </div>
    </>
  );
}
